/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import ButtonView, {
  ButtonViewState,
  ButtonViewType,
} from '@airasia-phoenix/react-native/components/ButtonView';
import IconView from '@airasia-phoenix/react-native/components/IconView';
import TextView from '@airasia-phoenix/react-native/components/TextView';
import { pd } from '@airasia-phoenix/react-native/utils';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, TouchableOpacity, View } from 'react-native';
import NotificationView from '@airasia-phoenix/react-native/components/NotificationView';
import { ChinaTermsAgreementView } from '../../components/ChinaTermsAgreementView';
import { EmailLoginFormView } from '../../components/EmailLoginFormView';
import { EmailDomainDismissOverlayView } from '../../components/EmailDomainDismissOverlayView';
import { MobileLoginFormView } from '../../components/MobileLoginFormView';
import { SocialProvidersView } from '../../components/SocialProvidersView';
import {
  usePrivacyStatementUrl,
  useTermsAndConditionsUrl,
  useTermsOfUseUrl,
} from '../../hooks/useAccountUrl';
import { useOpenUrl } from '../../hooks/useOpenUrl';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@airasia-rn/ui';
import { addDisclaimerHandlers } from '../../utils/signupLoginUtils';
import {
  createAnalyticsLogger,
  getSsoForgotPasswordLabels,
  getSsoLoginModalLabels,
  resolveSsoLoginMethod,
} from '../../utils/loginSignupAnalyticsUtils';
import styles from './styles';
import {
  EmailLoginMode,
  LoginNotification,
  LoginPageProps,
  SocialLoginResult,
} from './types';
import { buildLoginViewProps } from './loginViewProps';
import { useChinaTermsGate } from '../../hooks/useChinaTermsGate';
import { useEmailAuthFlow } from '../../hooks/useEmailAuthFlow';
import { useEmailLoginSubmit } from '../../hooks/useEmailLoginSubmit';
import { useKeepAccountRecovery } from '../../hooks/useKeepAccountRecovery';
import { useMobileLoginFlow } from '../../hooks/useMobileLoginFlow';
import { usePasskeyLoginFlow } from '../../hooks/usePasskeyLoginFlow';
import { useSocialLoginFlow } from '../../hooks/useSocialLoginFlow';
import { useSocialProviderVisibility } from '../../hooks/useSignupFeatureFlags';
import { useLogger } from '@airasia-rn/core-logger';
import type { SocialProvider } from '../../managers';
import { ACTIONS, CATEGORY, SCREEN_NAME } from '../../config/analytics';
import {
  applySocialProviderVisibility,
  hasVisibleSocialProviders,
} from '../../utils/socialProviders';
import { KeepAccountPopupView } from '../KeepAccountPopupView';

export default function LoginView(props: LoginPageProps) {
  const { t } = useTranslation('account');
  const emailLoginMode = props.emailLoginMode ?? EmailLoginMode.PASSWORDLESS;
  const isPasswordlessMode = emailLoginMode === EmailLoginMode.PASSWORDLESS;
  const openUrl = useOpenUrl();
  const { logEvent } = useLogger();
  const { currentLanguage } = useLocale();
  // Language-specific legal URLs, matching SignUpView
  const termsAndConditionsUrl = useTermsAndConditionsUrl();
  const termsOfUseUrl = useTermsOfUseUrl();
  const privacyStatementUrl = usePrivacyStatementUrl();

  const {
    headings,
    emailFieldProps,
    passwordFieldProps,
    mobileFieldProps,
    dialingCodeFieldProps,
    primaryButtonProps,
    loginWithMobileNumber,
    loginWithPasskey,
    socialLogins,
    disclaimer,
  } = useMemo(
    () =>
      buildLoginViewProps(props, t, {
        termsAndConditions: termsAndConditionsUrl,
        termsOfUse: termsOfUseUrl,
        privacyStatement: privacyStatementUrl,
        locale: currentLanguage,
      }),
    [
      props,
      t,
      termsAndConditionsUrl,
      termsOfUseUrl,
      privacyStatementUrl,
      currentLanguage,
    ],
  );

  const { visibility: socialProviderVisibility } =
    useSocialProviderVisibility();

  const {
    onCloseIconClick,
    onForgotPasswordClick,
    onSignUpClick,
    onLoginSuccess,
    onLoginError,
    onLoadingChange,
    onGoogleLoginRequest,
    onFacebookLoginRequest,
    onAppleLoginRequest,
    onPasswordLessEmailLoginSuccess,
    onTwoFactorRequired,
  } = props;

  // Login mode state - true for mobile, false for email
  const [isMobileLogin, setIsMobileLogin] = useState(
    props.initialLoginMethod === 'mobile',
  );

  // Notification state for top-level error display, shared by every login flow
  const [notification, setNotification] = useState<LoginNotification | null>(
    null,
  );

  /** Dismisses a stale submit error as soon as the user edits a field. */
  const clearNotification = useCallback(() => setNotification(null), []);

  const {
    keepAccountVisible,
    deleteScheduledAt,
    tryHandleScheduledDeletion,
    onKeepAccountNo,
    onKeepAccountYes,
  } = useKeepAccountRecovery();

  const { onKeepAccountVisibleChange } = props;
  useEffect(() => {
    onKeepAccountVisibleChange?.(keepAccountVisible);
  }, [keepAccountVisible, onKeepAccountVisibleChange]);

  const accountRecovery = useMemo(
    () => ({ tryHandleScheduledDeletion }),
    [tryHandleScheduledDeletion],
  );

  // China market: every login method waits for the T&C checkbox, as on signup.
  const {
    showChinaTerms,
    hasAcceptedChinaTerms,
    toggleChinaTerms,
    ensureChinaTermsAccepted,
  } = useChinaTermsGate(setNotification);

  const { submit: submitEmailLogin, isEmailLoginPending } = useEmailLoginSubmit(
    {
      emailLoginMode,
      onLoginSuccess,
      onLoginError,
      onPasswordLessEmailLoginSuccess,
      onTwoFactorRequired,
      setNotification,
      accountRecovery,
    },
  );

  const {
    email,
    password,
    emailErrorMessage,
    passwordErrorMessage,
    isSecure,
    setIsSecure,
    handleEmailChange,
    handlePasswordChange,
    handleSubmit: handleEmailLoginPress,
    emailFieldRef,
    showEmailDomainDropdown,
    emailDomainDropdownData,
    emailDomainDropdownHeight,
    handleEmailDomainSelect,
    dismissEmailDomainDropdown,
    handleEmailFocusChange,
    emailClearEndIcon,
    onEmailEndIconPress,
  } = useEmailAuthFlow({
    initialEmail: props.prefilledEmail ?? emailFieldProps.value,
    initialPassword: passwordFieldProps.value,
    requirePassword: emailLoginMode === EmailLoginMode.PASSWORD,
    messages: {
      required: t('loginPage.fieldRequired'),
      emailInvalid: t('loginPage.emailError'),
      passwordInvalid: t('loginPage.passwordError'),
    },
    onSubmit: submitEmailLogin,
    onEdit: clearNotification,
  });

  useEffect(() => {
    props.onEmailDomainDropdownChange?.(
      showEmailDomainDropdown,
      dismissEmailDomainDropdown,
    );
  }, [
    showEmailDomainDropdown,
    dismissEmailDomainDropdown,
    props.onEmailDomainDropdownChange,
  ]);

  const {
    mobileLogin,
    mobileLoginErrors,
    mobileErrorMessage,
    mobileFieldRef,
    countrySearch,
    setCountrySearch,
    filteredCountryData,
    handleMobileTextChange,
    handleDialingCodeChange,
    handleMobileLoginPress,
    isMobileLoginPending,
  } = useMobileLoginFlow({
    onMobileLoginSuccess: props.onMobileLoginSuccess,
    onMobileLoginPress: props.onMobileLoginPress,
    onLoginError,
    setNotification,
    accountRecovery,
  });

  const handleToggleLoginMode = useCallback(() => {
    setIsMobileLogin((isMobile) => !isMobile);
    setNotification(null);
  }, [setNotification]);

  // Sheets omit onSocialLoginSuccess and never handle 202 incomplete.
  const handleSocialLoginSuccess = useCallback(
    async (result: SocialLoginResult) => {
      if (props.onSocialLoginSuccess) {
        await props.onSocialLoginSuccess(result);
        return;
      }
      if (result.kind === 'authenticated') {
        await onLoginSuccess?.(result.data);
      }
    },
    [onLoginSuccess, props],
  );

  const { socialLoginHandlers, isSocialLoginPending } = useSocialLoginFlow({
    onGoogleLoginRequest,
    onFacebookLoginRequest,
    onAppleLoginRequest,
    onLoginSuccess: handleSocialLoginSuccess,
    onLoginError,
    setNotification,
    accountRecovery,
  });

  const { handlePasskeyLogin, isPasskeyLoginPending, passkeySupported } =
    usePasskeyLoginFlow({
      onLoginSuccess,
      onLoginError,
      setNotification,
      accountRecovery,
    });

  // Every flow reports the whole sign-in, not just its request: each stays true
  // until it has navigated, so the overlay never blinks off mid sign-in.
  const currentLoadingState =
    (isMobileLogin ? isMobileLoginPending : isEmailLoginPending) ||
    isSocialLoginPending ||
    isPasskeyLoginPending;

  useEffect(() => {
    onLoadingChange?.(currentLoadingState);
  }, [currentLoadingState, onLoadingChange]);

  const loginLabels = useMemo(
    () =>
      getSsoLoginModalLabels(
        resolveSsoLoginMethod({
          isMobileLogin,
          isPasswordless: isPasswordlessMode,
        }),
      ),
    [isMobileLogin, isPasswordlessMode],
  );

  const logLoginAnalytics = useMemo(
    () =>
      createAnalyticsLogger(logEvent, SCREEN_NAME.loginV2, CATEGORY.ssoLogin),
    [logEvent],
  );

  useEffect(() => {
    logLoginAnalytics(ACTIONS.screenView, loginLabels.screen);
  }, [loginLabels.screen, logLoginAnalytics]);

  const handleLoginPress = useCallback(() => {
    if (!ensureChinaTermsAccepted()) return;
    logLoginAnalytics(ACTIONS.tap, loginLabels.screen);
    if (isMobileLogin) {
      handleMobileLoginPress();
      return;
    }
    handleEmailLoginPress();
  }, [
    ensureChinaTermsAccepted,
    handleEmailLoginPress,
    handleMobileLoginPress,
    isMobileLogin,
    loginLabels.screen,
    logLoginAnalytics,
  ]);

  const handleForgotPasswordPress = useCallback(() => {
    logLoginAnalytics(ACTIONS.tap, getSsoForgotPasswordLabels().forgotPassword);
    onForgotPasswordClick?.();
  }, [logLoginAnalytics, onForgotPasswordClick]);

  const handleSignUpLinkPress = useCallback(() => {
    logLoginAnalytics(ACTIONS.tap, loginLabels.signUpLink);
    onSignUpClick?.();
  }, [loginLabels.signUpLink, logLoginAnalytics, onSignUpClick]);

  const handleToggleLoginModePress = useCallback(() => {
    logLoginAnalytics(
      ACTIONS.tap,
      isMobileLogin
        ? loginLabels.switchLoginEmail
        : loginLabels.switchLoginMobilePhone,
    );
    handleToggleLoginMode();
  }, [
    handleToggleLoginMode,
    isMobileLogin,
    loginLabels.switchLoginEmail,
    loginLabels.switchLoginMobilePhone,
    logLoginAnalytics,
  ]);

  const handlePasskeyPress = useCallback(() => {
    if (!ensureChinaTermsAccepted()) return;
    logLoginAnalytics(ACTIONS.tap, loginLabels.passkey);
    handlePasskeyLogin();
  }, [
    ensureChinaTermsAccepted,
    handlePasskeyLogin,
    loginLabels.passkey,
    logLoginAnalytics,
  ]);

  const handleSocialProviderPress = useCallback(
    (provider: SocialProvider) => {
      if (!ensureChinaTermsAccepted()) return;
      logLoginAnalytics(ACTIONS.tap, loginLabels.socialLogin(provider));
      socialLoginHandlers[provider]?.();
    },
    [
      ensureChinaTermsAccepted,
      loginLabels,
      logLoginAnalytics,
      socialLoginHandlers,
    ],
  );

  const disclaimerWithHandlers = useMemo(
    () => addDisclaimerHandlers(disclaimer, openUrl),
    [disclaimer, openUrl],
  );

  // Dynamic button text and props
  // Note: No need to depend on [t] since app restarts on language change
  const toggleButtonProps = useMemo(
    () => ({
      ...loginWithMobileNumber,
      type: loginWithMobileNumber?.type ?? ButtonViewType.SECONDARY_REGULAR,
      text: isMobileLogin
        ? t('loginPage.loginWithEmail')
        : t('loginPage.loginWithMobile'),
      startIcon: isMobileLogin
        ? {
            icon: pd.icon.main.communication.email,
            tint: loginWithMobileNumber?.startIcon?.tint,
          }
        : loginWithMobileNumber?.startIcon,
      onPress: handleToggleLoginModePress,
      disabled: loginWithMobileNumber?.disabled ?? false,
    }),
    [isMobileLogin, loginWithMobileNumber, handleToggleLoginModePress, t],
  );

  const currentPrimaryButtonProps = useMemo(
    () => ({
      ...primaryButtonProps,
      type: primaryButtonProps.type ?? ButtonViewType.PRIMARY_REGULAR,
      onPress: handleLoginPress,
      // Show disabled state when mobile login is in progress (following MoveWebPage pattern)
      disabled: isMobileLogin ? isMobileLoginPending : false,
    }),
    [primaryButtonProps, isMobileLogin, handleLoginPress, isMobileLoginPending],
  );

  const visibleSocialLogins = useMemo(
    () => applySocialProviderVisibility(socialLogins, socialProviderVisibility),
    [socialLogins, socialProviderVisibility],
  );

  const showSocialLogins = hasVisibleSocialProviders(visibleSocialLogins);

  return (
    <View style={styles.mainContainer}>
      <View style={styles.descContainer}>
        <View style={styles.rowContainer}>
          {Platform.OS === 'web' && onCloseIconClick && (
            <TouchableOpacity onPress={onCloseIconClick} testID="close-button">
              <IconView {...headings.closeIcon} />
            </TouchableOpacity>
          )}
          <TextView {...headings.greeting} testID="greeting-text" />
        </View>

        <View style={styles.rowContainer}>
          <TextView {...headings.prompt} testID="prompt-text" />
          <Pressable onPress={handleSignUpLinkPress}>
            <TextView {...headings.action} testID="signup-action" />
          </Pressable>
        </View>
      </View>

      {notification && (
        <NotificationView
          testID="login-error-notification"
          style={notification.type}
          text={notification.message}
          width="100%"
        />
      )}

      <View style={styles.formContainer}>
        {/* Conditional Login Form */}
        {isMobileLogin ? (
          <MobileLoginFormView
            mobileLogin={mobileLogin}
            errors={mobileLoginErrors}
            mobileErrorMessage={mobileErrorMessage}
            mobileFieldRef={mobileFieldRef}
            countrySearch={countrySearch}
            setCountrySearch={setCountrySearch}
            countryData={filteredCountryData}
            onMobileTextChange={handleMobileTextChange}
            onDialingCodeChange={handleDialingCodeChange}
            mobileFieldDisabled={mobileFieldProps.disabled ?? false}
            dialingCodeFieldDisabled={dialingCodeFieldProps.disabled ?? false}
          />
        ) : (
          <EmailLoginFormView
            emailFieldProps={emailFieldProps}
            passwordFieldProps={passwordFieldProps}
            forgotPasswordProps={headings.forgotPassword}
            email={email}
            password={password}
            emailErrorMessage={emailErrorMessage}
            passwordErrorMessage={passwordErrorMessage}
            onEmailChange={handleEmailChange}
            onPasswordChange={handlePasswordChange}
            showPassword={emailLoginMode === EmailLoginMode.PASSWORD}
            isSecure={isSecure}
            onToggleSecure={() => setIsSecure(!isSecure)}
            onForgotPasswordClick={handleForgotPasswordPress}
            emailFieldRef={emailFieldRef}
            showEmailDomainDropdown={showEmailDomainDropdown}
            emailDomainDropdownData={emailDomainDropdownData}
            emailDomainDropdownHeight={emailDomainDropdownHeight}
            onEmailDomainSelect={handleEmailDomainSelect}
            onEmailFocusChange={handleEmailFocusChange}
            emailClearEndIcon={emailClearEndIcon}
            onEmailEndIconPress={onEmailEndIconPress}
          />
        )}

        {/* Primary Button */}
        <ButtonView
          {...currentPrimaryButtonProps}
          testID="login-button"
          buttonViewState={
            Platform.OS === 'web' && isEmailLoginPending
              ? ButtonViewState.LOADING
              : ButtonViewState.LOADED
          }
        />

        {/* Continue as Guest (web only) */}
        {Platform.OS === 'web' && (
          <View style={styles.centerContainer}>
            <TextView
              {...headings.continueAsGuestProps}
              testID="continue-guest"
            />
          </View>
        )}

        {/* Divider */}
        {showSocialLogins ? (
          <View style={styles.dividerRow}>
            <View style={styles.divider} />
            <View style={styles.dividerTextContainer}>
              <TextView {...headings.dividerText} testID="divider-text" />
            </View>
            <View style={styles.divider} />
          </View>
        ) : null}

        {showSocialLogins ? (
          <SocialProvidersView
            providers={visibleSocialLogins}
            getLabel={(provider) =>
              visibleSocialLogins[provider]?.label?.text ?? provider
            }
            onProviderPress={handleSocialProviderPress}
          />
        ) : null}

        {/* Toggle Button and Passkey Button */}
        <ButtonView {...toggleButtonProps} testID="login-mobile" />
        <ButtonView
          {...loginWithPasskey}
          type={loginWithPasskey.type ?? ButtonViewType.SECONDARY_REGULAR}
          disabled={
            !passkeySupported ||
            loginWithPasskey.disabled ||
            isPasskeyLoginPending
          }
          onPress={handlePasskeyPress}
          testID="login-passkey"
        />

        {showChinaTerms ? (
          <ChinaTermsAgreementView
            isSelected={hasAcceptedChinaTerms}
            onToggle={toggleChinaTerms}
          />
        ) : null}

        {/* Disclaimer */}
        <TextView {...disclaimerWithHandlers} testID="disclaimer-text" />
      </View>
      <EmailDomainDismissOverlayView
        visible={showEmailDomainDropdown}
        onPress={dismissEmailDomainDropdown}
      />
      <KeepAccountPopupView
        visible={keepAccountVisible}
        deleteScheduledAt={deleteScheduledAt}
        onNo={onKeepAccountNo}
        onYesLogIn={onKeepAccountYes}
        onClose={onKeepAccountNo}
      />
    </View>
  );
}
